import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Sparkles, CheckCircle2, AlertCircle, XCircle, Search, X, Info } from 'lucide-react';
import { useAwardShortlistSuggestion, AwardShortlistBook } from '@/hooks/useAwardShortlistSuggestion';
import { toast } from '@/hooks/use-toast';
import { SearchAwardBookDialog } from '@/components/admin/SearchAwardBookDialog';
import { PRIZE_OPTIONS } from '@/lib/constants/awards';

interface Props {
  onApprove: (prizeName: string, year: number, books: AwardShortlistBook[]) => void;
}

export function AIAwardShortlistSuggestion({ onApprove }: Props) {
  const [prizeName, setPrizeName] = useState('');
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [suggestions, setSuggestions] = useState<AwardShortlistBook[]>([]);
  const [bookSelectorOpen, setBookSelectorOpen] = useState(false);
  const [replacingIndex, setReplacingIndex] = useState<number | null>(null);
  const [enrichmentProgress, setEnrichmentProgress] = useState<string>('');
  
  const { suggestShortlist, loading } = useAwardShortlistSuggestion();

  const handleGetSuggestions = async () => {
    try {
      setEnrichmentProgress('Starting...');
      const books = await suggestShortlist(
        prizeName,
        parseInt(year),
        (book, index, total) => {
          if (book.enrichmentStatus === 'enriching') {
            setEnrichmentProgress(`Enriching book ${index + 1} of ${total}: "${book.title}"...`);
          } else {
            const status = book.enrichmentStatus === 'matched' ? '✅' : 
                          book.enrichmentStatus === 'failed' ? '❌' : '⏳';
            setEnrichmentProgress(`${status} ${index + 1}/${total} complete`);
          }
          // Update suggestions incrementally so user sees progress
          setSuggestions(prev => {
            const updated = [...prev];
            updated[index] = book;
            return updated;
          });
        }
      );
      setSuggestions(books);
      setEnrichmentProgress('');
      
      const matchedCount = books.filter(b => b.bookId).length;
      toast({
        title: "Shortlist loaded",
        description: `Found ${books.length} books. ${matchedCount} automatically matched.`,
      });
    } catch (error: any) {
      setEnrichmentProgress('');
      toast({
        variant: "destructive",
        title: "Failed to load shortlist",
        description: error.message,
      });
    }
  };

  const handleReplaceBook = (index: number) => {
    setReplacingIndex(index);
    setBookSelectorOpen(true);
  };

  const handleBookSelected = (book: any) => {
    if (replacingIndex !== null && book) {
      const updated = [...suggestions];
      updated[replacingIndex] = {
        ...updated[replacingIndex],
        bookId: book.id,
        title: book.title,
        author: book.authors?.[0] || book.author,
        coverUrl: book.cover_url,
        matchConfidence: 'high',
        enrichmentStatus: 'matched',
      };
      setSuggestions(updated);
      setReplacingIndex(null);
    }
    setBookSelectorOpen(false);
  };

  const handleRemoveBook = (index: number) => {
    setSuggestions(suggestions.filter((_, i) => i !== index));
  };

  const handleApprove = () => {
    const unmatchedBooks = suggestions.filter(b => !b.bookId);
    if (unmatchedBooks.length > 0) {
      toast({
        title: "Some books not matched",
        description: `${unmatchedBooks.length} book(s) need to be found first`,
        variant: "destructive",
      });
      return;
    }

    onApprove(prizeName, parseInt(year), suggestions);
    setSuggestions([]);
    setPrizeName('');
    setYear(new Date().getFullYear().toString());
  };

  const getStatusIcon = (book: AwardShortlistBook) => {
    if (book.enrichmentStatus === 'enriching') {
      return <Loader2 className="h-4 w-4 animate-spin text-blue-600" />;
    }
    if (book.enrichmentStatus === 'matched' || book.matchConfidence === 'high') {
      return <CheckCircle2 className="h-4 w-4 text-green-600" />;
    }
    if (book.matchConfidence === 'medium') {
      return <AlertCircle className="h-4 w-4 text-yellow-600" />;
    }
    if (book.enrichmentStatus === 'failed' || book.matchConfidence === 'none') {
      return <XCircle className="h-4 w-4 text-red-600" />;
    }
    return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
  };

  const getStatusText = (book: AwardShortlistBook) => {
    if (book.enrichmentStatus === 'enriching') return 'Searching...';
    if (book.matchConfidence === 'high') return 'Auto-matched in database';
    if (book.matchConfidence === 'medium') return 'Possible match (verify)';
    if (book.matchConfidence === 'none') return 'Not found - manual search needed';
    return 'Pending...';
  };

  const canApprove = suggestions.length > 0 && suggestions.every(s => s.bookId);

  return (
    <Card className="border-primary/20">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary" />
          AI-Powered Shortlist Assistant
        </CardTitle>
        <CardDescription>
          AI finds books and tries multiple ISBNs automatically. ~6 books per shortlist.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-3 gap-4">
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="ai-prize">Prize Name</Label>
            <Select value={prizeName} onValueChange={setPrizeName}>
              <SelectTrigger>
                <SelectValue placeholder="Select a prize" />
              </SelectTrigger>
              <SelectContent>
                {PRIZE_OPTIONS.map(prize => (
                  <SelectItem key={prize} value={prize}>
                    {prize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ai-year">Year</Label>
            <Input
              id="ai-year"
              type="number"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              placeholder="2024"
            />
          </div>
        </div>

        <Button
          onClick={handleGetSuggestions}
          disabled={loading || !prizeName || !year}
          className="w-full"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {enrichmentProgress || 'Loading...'}
            </>
          ) : (
            <>
              <Sparkles className="mr-2 h-4 w-4" />
              Get AI Suggestions
            </>
          )}
        </Button>
        
        {suggestions.length > 0 && (
          <div className="space-y-4 pt-4 border-t">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                {suggestions.filter(s => s.bookId).length} of {suggestions.length} books matched
              </AlertDescription>
            </Alert>

            <div className="grid gap-3">
              {suggestions.map((book, idx) => (
                <div key={idx} className="flex items-start justify-between p-4 border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {getStatusIcon(book)}
                      <span className="font-semibold">
                        {book.position === 1 ? '🏆 ' : ''}{book.title}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-1">{book.author}</p>
                    <p className="text-xs text-muted-foreground">{getStatusText(book)}</p>
                    
                    {book.triedIsbns && book.triedIsbns.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Tried ISBNs: {book.triedIsbns.join(', ')}
                      </p>
                    )}
                    
                    {book.bookId && book.coverUrl && (
                      <img 
                        src={book.coverUrl} 
                        alt={book.title}
                        className="mt-2 w-16 h-24 object-cover rounded shadow-sm"
                      />
                    )}
                  </div>
                  <div className="flex gap-2 ml-4">
                    {!book.bookId && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleReplaceBook(idx)}
                        disabled={book.enrichmentStatus === 'enriching'}
                      >
                        <Search className="h-4 w-4 mr-1" />
                        Find
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveBook(idx)}
                      disabled={book.enrichmentStatus === 'enriching'}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            
            <Button
              onClick={handleApprove}
              disabled={!canApprove || loading}
              className="w-full"
            >
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Approve {suggestions.filter(s => s.bookId).length} Books
            </Button>
            
            {!canApprove && suggestions.length > 0 && (
              <p className="text-sm text-muted-foreground text-center">
                All books must be matched before approval
              </p>
            )}
          </div>
        )}

        <SearchAwardBookDialog 
          open={bookSelectorOpen}
          onOpenChange={(open) => {
            setBookSelectorOpen(open);
            if (!open) setReplacingIndex(null);
          }}
          onBookSelected={handleBookSelected}
        />
      </CardContent>
    </Card>
  );
}
